import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, AlertTriangle, Database, FileText, Package, TrendingUp, TrendingDown } from 'lucide-react';

const DatabaseCleaning = () => {
  const { token, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [cleanOptions, setCleanOptions] = useState({
    clean_pemasukan: true,
    clean_pengeluaran: true,
    clean_inventori: true,
    start_date: '',
    end_date: ''
  });

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/database-stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      toast.error('Gagal memuat statistik database');
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin, token]);

  const handleClean = async () => {
    if (confirmText !== 'HAPUS') {
      toast.error('Ketik "HAPUS" untuk konfirmasi');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/admin/clean-database', cleanOptions, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setIsConfirmOpen(false);
      setConfirmText('');
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal membersihkan database');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Akses ditolak. Hanya admin yang bisa mengakses halaman ini.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="database-cleaning-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black tracking-tight">Database Cleaning</h1>
        <p className="text-muted-foreground text-sm mt-1">Bersihkan data transaksi dari database</p>
      </div>

      {/* Warning */}
      <Card className="bg-amber-50 border-amber-300">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-bold text-amber-800">Peringatan!</p>
              <p className="text-sm text-amber-700">
                Menghapus data bersifat permanen dan tidak bisa dikembalikan. 
                Pastikan Anda sudah melakukan backup data sebelum melanjutkan.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Database Stats */}
      <Card className="bg-white border border-border/60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Statistik Database
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            <div className="p-4 bg-emerald-50 rounded-sm">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                <span className="text-xs font-semibold uppercase text-emerald-700">Pemasukan</span>
              </div>
              <p className="text-2xl font-black text-emerald-600">{stats?.pemasukan || 0}</p>
            </div>
            <div className="p-4 bg-rose-50 rounded-sm">
              <div className="flex items-center gap-2 mb-1">
                <TrendingDown className="h-4 w-4 text-rose-600" />
                <span className="text-xs font-semibold uppercase text-rose-700">Pengeluaran</span>
              </div>
              <p className="text-2xl font-black text-rose-600">{stats?.pengeluaran || 0}</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-sm">
              <div className="flex items-center gap-2 mb-1">
                <Package className="h-4 w-4 text-blue-600" />
                <span className="text-xs font-semibold uppercase text-blue-700">Inventori</span>
              </div>
              <p className="text-2xl font-black text-blue-600">{stats?.inventori || 0}</p>
            </div>
            <div className="p-4 bg-violet-50 rounded-sm">
              <div className="flex items-center gap-2 mb-1">
                <FileText className="h-4 w-4 text-violet-600" />
                <span className="text-xs font-semibold uppercase text-violet-700">Cabang</span>
              </div>
              <p className="text-2xl font-black text-violet-600">{stats?.cabang || 0}</p>
            </div>
            <div className="p-4 bg-zinc-100 rounded-sm">
              <div className="flex items-center gap-2 mb-1">
                <Database className="h-4 w-4 text-zinc-600" />
                <span className="text-xs font-semibold uppercase text-zinc-700">User</span>
              </div>
              <p className="text-2xl font-black text-zinc-600">{stats?.users || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clean Options */}
      <Card className="bg-white border border-border/60">
        <CardHeader>
          <CardTitle>Opsi Pembersihan</CardTitle>
          <CardDescription>Pilih data yang ingin dihapus</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="clean_pemasukan"
                checked={cleanOptions.clean_pemasukan}
                onCheckedChange={(checked) => setCleanOptions({ ...cleanOptions, clean_pemasukan: checked })}
              />
              <Label htmlFor="clean_pemasukan" className="cursor-pointer">
                Hapus data Pemasukan ({stats?.pemasukan || 0} data)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="clean_pengeluaran"
                checked={cleanOptions.clean_pengeluaran}
                onCheckedChange={(checked) => setCleanOptions({ ...cleanOptions, clean_pengeluaran: checked })}
              />
              <Label htmlFor="clean_pengeluaran" className="cursor-pointer">
                Hapus data Pengeluaran ({stats?.pengeluaran || 0} data)
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="clean_inventori"
                checked={cleanOptions.clean_inventori}
                onCheckedChange={(checked) => setCleanOptions({ ...cleanOptions, clean_inventori: checked })}
              />
              <Label htmlFor="clean_inventori" className="cursor-pointer">
                Hapus data Inventori ({stats?.inventori || 0} data)
              </Label>
            </div>
          </div>

          <div className="pt-4 border-t">
            <Label className="text-sm font-medium">Filter Berdasarkan Tanggal (Opsional)</Label>
            <p className="text-xs text-muted-foreground mb-3">Kosongkan untuk menghapus semua data</p>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Dari Tanggal</Label>
                <Input
                  type="date"
                  value={cleanOptions.start_date}
                  onChange={(e) => setCleanOptions({ ...cleanOptions, start_date: e.target.value })}
                  data-testid="clean-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Sampai Tanggal</Label>
                <Input
                  type="date"
                  value={cleanOptions.end_date}
                  onChange={(e) => setCleanOptions({ ...cleanOptions, end_date: e.target.value })}
                  data-testid="clean-end-date"
                />
              </div>
            </div>
          </div>

          <Button 
            variant="destructive" 
            onClick={() => setIsConfirmOpen(true)}
            disabled={!cleanOptions.clean_pemasukan && !cleanOptions.clean_pengeluaran && !cleanOptions.clean_inventori}
            className="mt-4"
            data-testid="clean-database-btn"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Bersihkan Database
          </Button>
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-rose-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Konfirmasi Penghapusan
            </DialogTitle>
            <DialogDescription>
              Tindakan ini tidak dapat dibatalkan. Data yang dihapus tidak bisa dikembalikan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-sm">
              <p className="text-sm text-rose-700">
                <strong>Data yang akan dihapus:</strong>
              </p>
              <ul className="text-sm text-rose-600 mt-2 space-y-1">
                {cleanOptions.clean_pemasukan && <li>• Pemasukan: {stats?.pemasukan || 0} data</li>}
                {cleanOptions.clean_pengeluaran && <li>• Pengeluaran: {stats?.pengeluaran || 0} data</li>}
                {cleanOptions.clean_inventori && <li>• Inventori: {stats?.inventori || 0} data</li>}
              </ul>
              {(cleanOptions.start_date || cleanOptions.end_date) && (
                <p className="text-xs text-rose-600 mt-2">
                  Filter: {cleanOptions.start_date || '...'} s/d {cleanOptions.end_date || '...'}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Ketik <strong className="text-rose-600">HAPUS</strong> untuk konfirmasi:</Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Ketik HAPUS"
                data-testid="confirm-delete-text"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setIsConfirmOpen(false)}>
              Batal
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleClean} 
              disabled={loading || confirmText !== 'HAPUS'}
              data-testid="confirm-clean-btn"
            >
              {loading ? 'Menghapus...' : 'Hapus Permanen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DatabaseCleaning;
